import React, { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';

/** Patrick's roof replacement: the example stages, in kronor. */
export const STAGE_GUIDE_EXAMPLE_AMOUNTS = [30000, 50000, 100000, 20000] as const;

const TIP_KEYS = ['result', 'scope', 'count', 'amount', 'language'] as const;
const TIPS_STORAGE_KEY = 'etappio-wizard-stage-guide-tips';

/** Open the first time the company creates stages, then folded; a choice is kept. */
const readTipsOpen = (): boolean => {
  try {
    const stored = window.localStorage.getItem(TIPS_STORAGE_KEY);
    return stored === null ? true : stored === 'open';
  } catch {
    return true;
  }
};

// The guide keeps its state while the page is open, so going back to step 2
// (or reopening the wizard) shows it as the user left it; only a new visit
// starts folded after the first time.
let tipsOpenThisVisit: boolean | null = null;

const storeTipsOpen = (open: boolean) => {
  tipsOpenThisVisit = open;
  try {
    window.localStorage.setItem(TIPS_STORAGE_KEY, open ? 'open' : 'closed');
  } catch {
    // Storage blocked: the choice lasts until reload.
  }
};

/** Kronor in the reader's language: "30 000 kr" / "SEK 30,000". */
export function useKronorFormat() {
  const { i18n } = useTranslation();
  const locale = i18n.language?.startsWith('en') ? 'en-GB' : 'sv-SE';
  return (amount: number) =>
    new Intl.NumberFormat(locale, { style: 'currency', currency: 'SEK', maximumFractionDigits: 0 }).format(amount);
}

/**
 * Guidance at the top of wizard step 2: how to split a project into stages,
 * with Patrick's tips and his roof replacement as a folded example. The
 * example is never filled in automatically.
 */
export function StageGuide() {
  const { t } = useTranslation();
  const ids = useId();
  const kronor = useKronorFormat();
  const [tipsOpen, setTipsOpen] = useState(() => tipsOpenThisVisit ?? readTipsOpen());
  const [exampleOpen, setExampleOpen] = useState(false);

  // Seen once: the next visit starts folded unless the user opens it again.
  // This visit keeps what is shown now.
  useEffect(() => {
    if (tipsOpenThisVisit === null) tipsOpenThisVisit = tipsOpen;
    try {
      if (window.localStorage.getItem(TIPS_STORAGE_KEY) === null) window.localStorage.setItem(TIPS_STORAGE_KEY, 'closed');
    } catch {
      // Storage blocked: open every time.
    }
    // Runs once per mount; the state at mount is what this visit starts with.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleTips = () => {
    setTipsOpen(open => {
      storeTipsOpen(!open);
      return !open;
    });
  };
  const total = STAGE_GUIDE_EXAMPLE_AMOUNTS.reduce((sum, amount) => sum + amount, 0);

  return (
    <section className="contract-wizard-guide mb-4" aria-labelledby={`${ids}-title`} data-testid="stage-guide">
      <div className="contract-wizard-guide-head">
        <span className="contract-wizard-guide-icon" aria-hidden="true">
          {/* Staircase: one step per stage, as in Etappio's mark. */}
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 20h4v-4h4v-4h4V8h4"></path>
          </svg>
        </span>
        <div className="contract-wizard-guide-heading">
          <h3 id={`${ids}-title`} className="contract-wizard-guide-title">{t('contracts.wizard.pricing.guide.title')}</h3>
          <p className="mb-0">{t('contracts.wizard.pricing.guide.intro')}</p>
        </div>
        <button
          type="button"
          className="btn btn-link contract-wizard-guide-toggle"
          aria-expanded={tipsOpen}
          aria-controls={`${ids}-tips`}
          onClick={toggleTips}
        >
          {t(tipsOpen ? 'contracts.wizard.pricing.guide.hideTips' : 'contracts.wizard.pricing.guide.showTips')}
        </button>
      </div>

      {tipsOpen && (
        <ul id={`${ids}-tips`} className="contract-wizard-guide-tips">
          {TIP_KEYS.map(key => (
            <li key={key}>
              <i className="feather-check" aria-hidden="true"></i>
              <span>
                <strong>{t(`contracts.wizard.pricing.guide.tips.${key}.label`)}</strong>{' '}
                {t(`contracts.wizard.pricing.guide.tips.${key}.text`)}
              </span>
            </li>
          ))}
        </ul>
      )}

      <div className="contract-wizard-guide-example">
        <button
          type="button"
          className="contract-wizard-guide-example-toggle"
          aria-expanded={exampleOpen}
          aria-controls={`${ids}-example`}
          onClick={() => setExampleOpen(open => !open)}
        >
          <i className="feather-file-text" aria-hidden="true"></i>
          <span className="contract-wizard-guide-example-heading">
            <span className="contract-wizard-guide-example-label">{t('contracts.wizard.pricing.guide.example.toggle')}</span>
            <span className="contract-wizard-guide-example-total">{kronor(total)}</span>
          </span>
          <i className={`feather-chevron-${exampleOpen ? 'up' : 'down'}`} aria-hidden="true"></i>
        </button>
        {exampleOpen && (
          <div id={`${ids}-example`} className="contract-wizard-guide-example-body">
            <ol className="contract-wizard-guide-example-stages">
              {STAGE_GUIDE_EXAMPLE_AMOUNTS.map((amount, index) => (
                <li key={amount}>
                  <span className="contract-wizard-guide-example-number" aria-hidden="true">{index + 1}</span>
                  <span className="contract-wizard-guide-example-text">
                    <strong>{t(`contracts.wizard.pricing.guide.example.stages.${index + 1}.title`)}</strong>
                    <span className="d-block">{t(`contracts.wizard.pricing.guide.example.stages.${index + 1}.description`)}</span>
                  </span>
                  <span className="contract-wizard-guide-example-amount">{kronor(amount)}</span>
                </li>
              ))}
            </ol>
            <p className="form-text mb-0">{t('contracts.wizard.pricing.guide.example.adapt')}</p>
          </div>
        )}
      </div>
    </section>
  );
}

/** Tests only: forget the state of this page visit. */
export const resetStageGuideVisitForTests = () => {
  tipsOpenThisVisit = null;
};
